// @spec GHG-STR-050
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useEmissionTargets } from '@/features/ghg/api/use-emission-targets';
import { useReductionInitiatives } from '@/features/ghg/api/use-reduction-initiatives';
import { useCreateEmissionScenario } from '@/features/ghg/api/use-scenarios';
import { ScenarioForm } from '@/features/ghg/components/ScenarioForm';
import type { EmissionScenarioFormValues } from '@/features/ghg/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/scenarios/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.newScenario'),
  }),
  component: NewScenarioPage,
});

/**
 * New Scenario creation page.
 *
 * Provides a form for creating a new emission scenario with
 * initiative selection and custom adjustments.
 */
function NewScenarioPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const createScenario = useCreateEmissionScenario();
  const { targets } = useEmissionTargets({ status: 'active' });
  const { initiatives } = useReductionInitiatives();
  const actionLevers = initiatives.map((initiative) => ({
    id: initiative.id,
    name: initiative.name,
    estimatedAnnualReduction: initiative.estimatedAnnualReduction,
  }));

  const handleBack = () => {
    void navigate({ to: '/ghg/scenarios' });
  };

  const handleSubmit = (data: EmissionScenarioFormValues) => {
    createScenario.mutate(
      {
        name: data.name,
        description: data.description || undefined,
        baseYear: data.baseYear,
        projectionEndYear: data.projectionEndYear,
        targetId: data.targetId || undefined,
        initiatives: data.initiatives,
        customAdjustments: data.customAdjustments,
      },
      {
        onSuccess: (scenario) => {
          toast.success(t('toast.scenarioCreatedSuccessfully'));
          void navigate({
            to: '/ghg/scenarios/$scenarioId',
            params: { scenarioId: scenario.id },
          });
        },
        onError: () => {
          toast.error(t('toast.failedToCreateScenario'));
        },
      }
    );
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('newScenario.back')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('newScenario.title')}</h1>
          <p className="text-muted-foreground">
            {t('newScenario.description')}
          </p>
        </div>

        {/* Form */}
        <Card>
          <CardContent className="pt-6">
            <ScenarioForm
              targets={targets}
              initiatives={actionLevers}
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isSubmitting={createScenario.isPending}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
